import { FormEvent, useState } from "react";

import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isAfter, isBefore, sub } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import { MenuItem, Stack } from "@mui/material";
import { useToast } from "../../../../../Hooks/toast";
import { useFilter } from "../../../../../Hooks/useFilter";
import { cleanTaxIDMask, handleTaxIDInput } from "../../../../../Utils";
import { formatInputValue } from "../../../../../Utils/formatInputValue";
import { simpleConvertToCents } from "../../../../../Utils/formatters";
import {
  ButtonsWrapper,
  DatePickerContainer,
  DatePickerField,
  Field,
  FlexDivCustom,
  Form,
  StyledTextField,
} from "../styles";

interface InvoiceFilterProps {
  tableName: string;
  setPageToFirst: () => void;
}

export const InvoiceFilter = ({
  tableName,
  setPageToFirst,
}: InvoiceFilterProps) => {
  const { getFilterObj, handleApplyFilters, handleRemoveFilters } = useFilter();

  const filterObj = getFilterObj(tableName) as any;

  const { addToast } = useToast();

  const [startDate, setStartDate] = useState<Date | null>(() => {
    return filterObj?.startDate !== null &&
      filterObj?.startDate !== undefined &&
      filterObj?.startDate !== ""
      ? new Date(filterObj.startDate.replace(/-/g, "/").replace(/T.+/, ""))
      : null;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    return filterObj?.endDate !== null &&
      filterObj?.endDate !== undefined &&
      filterObj?.endDate !== ""
      ? new Date(filterObj.endDate.replace(/-/g, "/").replace(/T.+/, ""))
      : null;
  });
  const [startAmount, setStartAmount] = useState<number | null>(() => {
    return filterObj?.startAmount !== null &&
      filterObj?.startAmount !== undefined &&
      filterObj?.startAmount !== ""
      ? formatInputValue(filterObj.startAmount.toString())
      : null;
  });
  const [endAmount, setEndAmount] = useState<number | null>(() => {
    return filterObj?.endAmount !== null &&
      filterObj?.endAmount !== undefined &&
      filterObj?.endAmount !== ""
      ? formatInputValue(filterObj.endAmount.toString())
      : null;
  });
  const [status, setStatus] = useState<number | null>(filterObj?.status || null);

  const [protocol, setProtocol] = useState<number | null>(filterObj?.protocol || null);
  const [document, setDocument] = useState<string>(() => {
    return filterObj?.document !== null &&
      filterObj?.document !== undefined &&
      filterObj?.document !== ""
      ? cleanTaxIDMask(filterObj.document)
      : ""
  });
  const [accessKey, setAccessKey] = useState<string>(filterObj?.accessKey || "");

  const [manifested, setManifested] = useState<boolean | null>(() => {
    if (filterObj?.manifested === true || filterObj?.manifested === false)
      return filterObj.manifested;
    return null;
  });
  const [recipient, setRecipient] = useState<string | null>(filterObj?.recipient || "");

  const handleCustomStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleCustomEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleDeleteFilters = () => {
    if (Object.keys(filterObj).length === 0) {
      addToast({
        title: "Nenhum filtro aplicado.",
        description: "Não tem filtro aplicado para ser removido.",
        type: "info",
      });
      return;
    }

    handleRemoveFilters(tableName, {
      startDate: null,
      endDate: null,
      protocol: "",
      startAmount: "",
      endAmount: "",
      accessKey: "",
      status: "",
      document: "",
      manifested: null,
      recipient: "",
    });
    setStartDate(null);
    setEndDate(null);
    setProtocol(null);
    setStartAmount(null);
    setEndAmount(null);
    setAccessKey("");
    setStatus(null);
    setDocument("");
    setManifested(null);
    setRecipient("");
  };

  const handleSubmitForm = (e: FormEvent) => {
    e.preventDefault();

    const queryParams: any = {
      startDate: startDate === null ? "" : format(startDate, "yyyy-MM-dd"),
      endDate: endDate === null ? "" : format(endDate, "yyyy-MM-dd"),
      protocol: protocol,
      startAmount: startAmount === null ? "" : simpleConvertToCents(startAmount),
      endAmount: endAmount === null ? "" : simpleConvertToCents(endAmount),
      accessKey: accessKey.trim(),
      status: status,
      document: document.replace(/\D/g, ""),
      manifested: manifested === null ? "" : manifested,
      recipient: recipient?.trim() || "",
    };

    const hasValue = Object.values(queryParams).some(value =>
      value !== null && value !== ""
    );

    //verifica se algum filtro foi aplicado
    if (!hasValue) {
      addToast({
        type: "info",
        title: "Nenhum filtro foi aplicado!",
        description: "Por favor insira algum parâmetro.",
      });

      return;
    }

    //Verifica se o filtro ja foi aplicado
    if (JSON.stringify(queryParams) === JSON.stringify(filterObj)) {
      addToast({
        type: "info",
        title: "Filtro já aplicado!",
        description: "Por favor insira outros parâmetros.",
      });

      return;
    }
    //Verifica se as datas estão em um formato válido
    // Valida datas apenas se não forem null
    if (startDate !== null) {
      const regex = /^\d{4}-([0]\d|1[0-2])-([0-2]\d|3[01])$/;
      if (
        !regex.test(queryParams.startDate) ||
        isBefore(
          new Date(queryParams.startDate),
          sub(new Date(), { years: 1 })
        ) ||
        isAfter(
          new Date(queryParams.startDate.replace(/-/g, "/").replace(/T.+/, "")),
          new Date()
        )
      ) {
        addToast({
          type: "info",
          title: "Data Inválida!",
          description: "Por favor insira uma data inicial válida.",
        });
        return;
      }
    }

    if (endDate !== null) {
      const regex = /^\d{4}-([0]\d|1[0-2])-([0-2]\d|3[01])$/;
      if (
        !regex.test(queryParams.endDate) ||
        isBefore(
          new Date(queryParams.endDate.replace(/-/g, "/").replace(/T.+/, "")),
          sub(new Date(), { years: 1 })
        ) ||
        isAfter(
          new Date(queryParams.endDate.replace(/-/g, "/").replace(/T.+/, "")),
          new Date()
        )
      ) {
        addToast({
          type: "info",
          title: "Data Inválida!",
          description: "Por favor insira uma data final válida.",
        });
        return;
      }
    }

    // Verifica se a data inicial não é maior que a data final (se ambas existirem)
    if (startDate !== null && endDate !== null) {
      if (
        isAfter(
          new Date(queryParams.startDate.replace(/-/g, "/").replace(/T.+/, "")),
          new Date(queryParams.endDate.replace(/-/g, "/").replace(/T.+/, ""))
        )
      ) {
        addToast({
          type: "info",
          title: "Data Inválida!",
          description: "Data inicial não pode ser maior que data final.",
        });
        return;
      }
    }

    handleApplyFilters(tableName, queryParams);
    setPageToFirst();
  };

  return (
    <Form onSubmit={handleSubmitForm}>
      <FlexDivCustom>
        <Field>
          <StyledTextField
            id="accessKey"
            label="Chave de Acesso"
            value={accessKey}
            onChange={(e) => {
              setAccessKey(e.target.value);
            }}
            name="accessKey"
            type="text"
          />
        </Field>
        <Field>
          <StyledTextField
            id="protocol"
            label="Protocolo"
            value={protocol}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/\D/g, "");
              setProtocol(onlyDigits === "" ? null : Number(onlyDigits));
            }}
            name="protocol"
            type="text"
          />
        </Field>

        <Field>
          <StyledTextField
            id="status"
            select
            label="Status"
            value={status === null ? "" : status}
            onChange={(e) => {
              const value = e.target.value;
              setStatus(value === "" ? null : Number(value));
            }}
          >
            <MenuItem value=""></MenuItem>
            <MenuItem value="1">Enviada</MenuItem>
            <MenuItem value="2">Rejeitada</MenuItem>
            <MenuItem value="3">Inválida</MenuItem>
            <MenuItem value="4">Aprovada</MenuItem>
            <MenuItem value="5">Antecipada</MenuItem>
            <MenuItem value="6">Validada</MenuItem>
            <MenuItem value="7">Aguardando Validação</MenuItem>
          </StyledTextField>
        </Field>
      </FlexDivCustom>
      <FlexDivCustom>
        <Field>
          <StyledTextField
            id="startAmount"
            label="Valor Min."
            value={startAmount === null ? "" : startAmount}
            onChange={(e) => {
              setStartAmount(formatInputValue(e.target.value));
            }}
            name="startAmount"
            type="text"
            placeholder="Valor (R$)"
            aria-label="minAmount-input"
          />
        </Field>
        <Field>
          <StyledTextField
            id="endAmount"
            variant={"outlined"}
            label={"Valor Máx."}
            value={endAmount === null ? "" : endAmount}
            onChange={(e) => {
              setEndAmount(formatInputValue(e.target.value));
            }}
            name="endAmount"
            type="text"
            placeholder="Valor (R$)"
            aria-label="maxAmount-input"
          />
        </Field>
        <Field>
          <StyledTextField
            id="document"
            label="Documento"
            value={document}
            onChange={(e) => {
              setDocument(handleTaxIDInput(e.target.value));
            }}
            name="document"
            type="text"
          />
        </Field>
      </FlexDivCustom>
      <FlexDivCustom>
        <Field>
          <StyledTextField
            id="manifested"
            select
            label="Manifesto"
            value={manifested === null ? "" : String(manifested)}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "") {
                setManifested(null);
              } else {
                setManifested(val === "true");
              }
            }}
          >
            <MenuItem value=""></MenuItem>
            <MenuItem value="true">Manifestada</MenuItem>
            <MenuItem value="false">Não Manifestada</MenuItem>
          </StyledTextField>
        </Field>
        <Field>
          <StyledTextField
            id="recipient"
            label="Destinatário"
            value={recipient}
            onChange={(e) => {
              setRecipient(e.target.value);
            }}
            name="recipient"
            type="text"
          />
        </Field>
      </FlexDivCustom>
      <DatePickerContainer>
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
          <Stack
            direction="row"
            style={{ gap: "2.2rem" }}
            alignItems={"center"}
          >
            <Field>
              <DesktopDatePicker
                label="Data inicial"
                value={startDate}
                minDate={sub(new Date(), { years: 1 })}
                maxDate={new Date()}
                onChange={handleCustomStartDateChange}
                renderInput={(params: any) => <DatePickerField {...params} />}
              />
            </Field>
            <Field>
              <DesktopDatePicker
                label="Data final"
                value={endDate}
                minDate={sub(new Date(), { years: 1 })}
                maxDate={new Date()}
                onChange={handleCustomEndDateChange}
                renderInput={(params: any) => <DatePickerField {...params} />}
              />
            </Field>
          </Stack>
        </LocalizationProvider>
      </DatePickerContainer>
      <ButtonsWrapper>
        <button type="submit">
          <svg style={{ marginRight: 8, verticalAlign: 'middle' }} xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" fill="currentColor"><path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z"/></svg>
          Aplicar filtros
        </button>
        <button type="button" onClick={handleDeleteFilters}>
          <svg style={{ marginRight: 8, verticalAlign: 'middle' }} xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" fill="currentColor"><path d="M280-160q-33 0-56.5-23.5T200-240v-80h80v80h400v-80h80v80q0 33-23.5 56.5T680-160H280Zm200-160q-100 0-170-70t-70-170v-120q0-17 11.5-28.5T280-720h400q17 0 28.5 11.5T720-680v120q0 100-70 170t-170 70Zm0-80q67 0 113.5-46.5T640-600v-80H320v80q0 67 46.5 113.5T480-400Z"/></svg>
          Remover filtros
        </button>
      </ButtonsWrapper>
    </Form>
  );
};
